import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Panel } from '../components/Panel'
import { Metric } from '../components/Metric'
import { StatusDot } from '../components/StatusDot'
import { PubBadge } from '../components/PubBadge'
import { api, type DashboardSnapshot, type ReconciliationReport } from '../api/client'
import { fmtInt, fmtMoney, fmtRelative, publicationColor } from '../lib/format'
import { YesterdayOrdersPanel } from './YesterdayOrders'

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardSnapshot>('/api/dashboard'),
    refetchInterval: 30_000,
  })

  return (
    <div className="p-8 space-y-8 max-w-[1400px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Status · 2026 Q2</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
          Active Circulation
          <span className="text-amber-glow"> ∙ </span>
          <span className="italic font-light">all titles</span>
        </h1>
      </header>

      {/* Sync Health + Daily Change Report moved to the Sync Health page
          (left-nav · bottom item). Dashboard focuses on composition, not
          pipeline telemetry. */}

      {error && (
        <Panel label="SYSTEM ERROR" accent="red">
          <div className="font-mono text-[12px] text-radar-red">
            Could not load dashboard: {String((error as Error).message)}
          </div>
        </Panel>
      )}

      {/* Publication grid — current status */}
      <section>
        <div className="label mb-3">Per-Title Status · Current</div>
        <div className="space-y-4">
          {data && <AggregateStatusRow data={data} />}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(data?.publications ?? Array(4).fill(null)).map((pub, i) => (
              <PublicationCard key={pub?.slug ?? i} pub={pub} loading={isLoading} />
            ))}
          </div>
        </div>
      </section>

      <YesterdayOrdersPanel />

      {/* 12-month forecast per title */}
      <section>
        <div className="label mb-3">Per-Title · 12-Month Forecast</div>
        <div className="space-y-4">
          {data && <AggregateForecastRow data={data} />}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(data?.publications ?? Array(4).fill(null)).map((pub, i) => (
              <Forecast12moCard key={pub?.slug ?? i} pub={pub} loading={isLoading} />
            ))}
          </div>
        </div>
      </section>

      {/* Reachability matrix — auto-renew × email × publication */}
      {data && (
        <section>
          <div className="label mb-3">Active Subscribers · Reachability × Renewal Mode</div>
          <ReachabilityMatrix data={data.reachability} />
        </section>
      )}

      {/* Last payment method — by publication */}
      {data && (
        <section>
          <div className="label mb-3">Active Subscriptions · Last Payment Method</div>
          <PaymentMethodMatrix data={data.payment_methods} />
        </section>
      )}

      {/* Upcoming expirations, next 12 months — broken down by cohort */}
      {data && (
        <section>
          <div className="label mb-3">Upcoming Expirations · Next 12 Months</div>
          <UpcomingExpirations data={data.upcoming_expirations} />
        </section>
      )}

      {/* Mailing-pull cohort: who newly expired this month, and of those
          how many will run through SimpleCirc AR billing this cycle. */}
      {data && (
        <section>
          <div className="label mb-3">
            Mailing Pull · {data.ar_process_current_month.cohort_month} Cohort
          </div>
          <ARProcessCurrentMonth data={data.ar_process_current_month} />
        </section>
      )}

      {/* AR Cycle Effectiveness: walk through SC's retry pipeline stage
          by stage so ops can see where subs convert vs drop out. */}
      {data && data.ar_cycle_effectiveness.cohorts.length > 0 && (
        <section>
          <div className="label mb-3">AR Cycle Effectiveness</div>
          <ARCycleEffectiveness data={data.ar_cycle_effectiveness} />
        </section>
      )}

      {/* Track finder review queue (RC Car Action) */}
      {data && (data.totals.tracks_queue_open ?? 0) > 0 && (
        <section>
          <Link
            to="/editorial/pit-lane/tracks"
            className="block border border-amber-glow/40 bg-amber-glow/5 px-4 py-3 rounded-sm hover:bg-amber-glow/10 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="label text-amber-glow">RC Car Action · Track Finder</div>
                <div className="text-paper text-[14px] mt-1">
                  <span className="font-mono tabular-nums text-amber-glow">{fmtInt(data.totals.tracks_queue_open ?? 0)}</span>{' '}
                  waiting for review
                  {(data.totals.tracks_queue_overdue ?? 0) > 0 && (
                    <span className="text-amber-glow">
                      {' '}· <span className="font-mono tabular-nums">{fmtInt(data.totals.tracks_queue_overdue ?? 0)}</span> overdue
                    </span>
                  )}
                </div>
              </div>
              <div className="label-amber">Review →</div>
            </div>
          </Link>
        </section>
      )}

      {/* Data quality — duplicate subscribers pending review */}
      {data && (data.totals.pending_duplicates ?? 0) > 0 && (
        <section>
          <Link
            to="/duplicates"
            className="block border border-amber-glow/40 bg-amber-glow/5 px-4 py-3 rounded-sm hover:bg-amber-glow/10 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="label text-amber-glow">Data Quality · Duplicate subscribers</div>
                <div className="text-paper text-[14px] mt-1">
                  <span className="font-mono tabular-nums text-amber-glow">
                    {fmtInt(data.totals.pending_duplicates ?? 0)}
                  </span>{' '}
                  candidate pair{data.totals.pending_duplicates === 1 ? '' : 's'} pending review
                </div>
                <div className="text-paper-dim text-[11px] mt-1">
                  Same person signed up more than once. Review and merge to deduplicate your active count.
                </div>
              </div>
              <div className="label-amber">Review →</div>
            </div>
          </Link>
        </section>
      )}

      {/* Two-column: events + syncs */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <Panel
            label="Recent Events"
            accent="cyan"
            right={<Link to="/events" className="label-amber">All →</Link>}
            bodyClassName="p-0"
          >
            <EventTable events={data?.recent_events ?? []} loading={isLoading} />
          </Panel>
        </div>
        <div className="lg:col-span-2">
          <Panel
            label="Recent Sync Runs"
            accent="violet"
            right={<Link to="/sync" className="label-amber">All →</Link>}
            bodyClassName="p-0"
          >
            <SyncTable runs={data?.recent_syncs ?? []} loading={isLoading} />
          </Panel>
        </div>
      </section>
    </div>
  )
}

function PublicationCard({
  pub,
  loading,
}: {
  pub: DashboardSnapshot['publications'][number] | null
  loading: boolean
}) {
  if (!pub) {
    return (
      <div className="border border-ink-500 bg-ink-800/60 h-[168px] flex items-center justify-center">
        <span className="label text-paper-trace">{loading ? 'LOADING…' : '—'}</span>
      </div>
    )
  }

  const color = publicationColor(pub.slug)
  const syncStatus = pub.last_sync.status
  const tone = syncStatus === 'ok' ? 'ok' : syncStatus === 'error' ? 'err' : 'muted'

  return (
    <Link to={`/titles/${pub.slug}`} className="block hover:brightness-110 transition-all">
    <Panel accent="none" className="group cursor-pointer">
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="label" style={{ color }}>{pub.slug.replace(/_/g, ' ')}</div>
            <div className="font-serif text-[20px] leading-none mt-1 tracking-tight text-paper">
              {pub.name}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <PubBadge slug={pub.slug} compact />
            {pub.current_renewal_price !== null && pub.current_renewal_price !== undefined && (
              <div className="flex items-baseline gap-1">
                <span className="label text-paper-faint">renews @</span>
                <span
                  className="font-mono text-[13px] font-semibold tabular-nums"
                  style={{ color }}
                >
                  {fmtMoney(pub.current_renewal_price, false, 2)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <CompactStat label="Active" value={fmtInt(pub.active_count)} tone="text-amber-glow" />
          <CompactStat
            label="Remit"
            value={fmtMoney(pub.revenue_active_remit, true)}
            tone="text-radar-green"
          />
          <CompactStat
            label="Avg / yr"
            value={
              pub.active_count > 0
                ? fmtMoney(pub.revenue_active_remit / pub.active_count, false, 2)
                : '—'
            }
            tone="text-radar-cyan"
          />
        </div>

        <CategoryBreakdownTable
          compact
          rows={[
            {
              label: 'Auto-renew',
              count: pub.current_breakdown.auto_count,
              revenue: pub.current_breakdown.auto_revenue,
              tone: 'text-amber-glow',
            },
            {
              label: 'Manual · email',
              count: pub.current_breakdown.manual_with_email_count,
              revenue: pub.current_breakdown.manual_with_email_revenue,
              tone: 'text-radar-cyan',
            },
            {
              label: 'Manual · no email',
              count: pub.current_breakdown.manual_no_email_count,
              revenue: pub.current_breakdown.manual_no_email_revenue,
              tone: 'text-radar-violet',
            },
            {
              label: 'Agency',
              count: pub.current_breakdown.agency_count,
              revenue: pub.current_breakdown.agency_remit_revenue,
              tone: 'text-radar-red',
            },
          ]}
        />

        <div className="flex items-center justify-between pt-2 border-t border-ink-500/60">
          <span className="flex items-center gap-2">
            <StatusDot tone={tone} pulse={tone === 'ok'} />
            <span className="label">Sync</span>
          </span>
          <span className="font-mono text-[10px] text-paper-dim tabular-nums">
            {pub.last_sync.at
              ? `${fmtRelative(pub.last_sync.at)} · +${pub.last_sync.added} / -${pub.last_sync.removed}`
              : 'NEVER'}
          </span>
        </div>
        <div className="text-right pt-1">
          <span className="label-amber group-hover:underline">Drill down →</span>
        </div>
      </div>
    </Panel>
    </Link>
  )
}

function EventTable({
  events,
  loading,
}: {
  events: DashboardSnapshot['recent_events']
  loading: boolean
}) {
  if (loading && events.length === 0) return <EmptyRow label="Loading…" />
  if (events.length === 0) return <EmptyRow label="No events yet" />
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-ink-500">
          <HeaderCell>When</HeaderCell>
          <HeaderCell>Source</HeaderCell>
          <HeaderCell>Event</HeaderCell>
          <HeaderCell>Pub</HeaderCell>
          <HeaderCell>Status</HeaderCell>
        </tr>
      </thead>
      <tbody>
        {events.slice(0, 8).map((e) => (
          <tr key={e.id} className="border-b border-ink-500/40 hover:bg-ink-700/40">
            <Cell mono>{fmtRelative(e.received_at)}</Cell>
            <Cell mono subtle>{e.source}</Cell>
            <Cell mono>{e.event_type}</Cell>
            <Cell>{e.publication_slug ? <PubBadge slug={e.publication_slug} compact /> : '—'}</Cell>
            <Cell>
              <EventStatusBadge status={e.processed_status} />
            </Cell>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function SyncTable({
  runs,
  loading,
}: {
  runs: DashboardSnapshot['recent_syncs']
  loading: boolean
}) {
  if (loading && runs.length === 0) return <EmptyRow label="Loading…" />
  if (runs.length === 0) return <EmptyRow label="No sync runs yet" />
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-ink-500">
          <HeaderCell>When</HeaderCell>
          <HeaderCell align="right">+Added</HeaderCell>
          <HeaderCell align="right">-Removed</HeaderCell>
          <HeaderCell>Status</HeaderCell>
        </tr>
      </thead>
      <tbody>
        {runs.slice(0, 8).map((r) => (
          <tr key={r.id} className="border-b border-ink-500/40 hover:bg-ink-700/40">
            <Cell mono>{fmtRelative(r.started_at)}</Cell>
            <Cell mono align="right" className="text-radar-green">+{fmtInt(r.delta_added)}</Cell>
            <Cell mono align="right" className="text-radar-red">-{fmtInt(r.delta_removed)}</Cell>
            <Cell>
              <EventStatusBadge status={r.status} />
            </Cell>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function HeaderCell({
  children, align = 'left',
}: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`label py-2 px-4 font-normal text-${align} first:pl-4 last:pr-4`}
    >
      {children}
    </th>
  )
}

function Th({
  children, align = 'left', className = '',
}: {
  children?: React.ReactNode
  align?: 'left' | 'right'
  className?: string
}) {
  return (
    <th className={`label py-2 px-4 font-normal text-${align} ${className}`}>
      {children}
    </th>
  )
}

function Cell({
  children, mono, subtle, align = 'left', className = '',
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  align?: 'left' | 'right'
  className?: string
}) {
  return (
    <td
      className={`py-2 px-4 text-${align}
        ${mono ? 'font-mono text-[11px] tabular-nums' : 'text-[13px]'}
        ${subtle ? 'text-paper-dim' : 'text-paper'}
        ${className}`}
    >
      {children}
    </td>
  )
}

function EmptyRow({ label }: { label: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <span className="label text-paper-trace">{label}</span>
    </div>
  )
}

export function DailyChangePanel() {
  const { data: persisted } = useQuery({
    queryKey: ['reconciliation-latest'],
    queryFn: () => api.get<ReconciliationReport | null>('/api/reconciliation/latest'),
    refetchInterval: 60_000,
  })
  // If there's no persisted report yet (first-run, or import still pending),
  // fall back to a filesystem preview so the user sees what's about to change.
  const { data: preview } = useQuery({
    queryKey: ['reconciliation-preview'],
    queryFn: () =>
      api.get<{
        prev_filename: string
        current_filename: string
        prev_rows: number
        current_rows: number
        net_delta: number
        totals: ReconciliationReport['totals']
        by_publication: ReconciliationReport['by_publication']
        note: string
      }>('/api/reconciliation/preview-latest').catch(() => null),
    enabled: !persisted,
    staleTime: 5 * 60 * 1000,
  })
  const report: (ReconciliationReport & { isPreview?: boolean }) | null = persisted
    ? persisted
    : preview
      ? {
          id: 0,
          generated_at: new Date().toISOString(),
          prev_import_id: null,
          current_import_id: 0,
          prev_filename: preview.prev_filename,
          current_filename: preview.current_filename,
          prev_rows: preview.prev_rows,
          current_rows: preview.current_rows,
          net_delta: preview.net_delta,
          totals: preview.totals,
          by_publication: preview.by_publication,
          error: null,
          isPreview: true,
        }
      : null
  if (!report) return null
  const t = report.totals
  const transitions = Object.entries(t.status_transitions ?? {})
    .sort((a, b) => b[1] - a[1])
  return (
    <section>
      <div className="label mb-3">
        Daily Change Report
        {report.isPreview ? (
          <span className="text-paper-trace ml-2">· preview (import pending)</span>
        ) : (
          <span className="text-paper-trace ml-2">· {fmtRelative(report.generated_at)}</span>
        )}
      </div>
      <Panel accent="cyan">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr_1fr] gap-5">
          <div>
            <div className="label text-paper-trace mb-1">
              {report.prev_filename ?? '—'}  →  {report.current_filename}
            </div>
            <div className="flex items-baseline gap-4 mt-3">
              <div>
                <div className="label text-paper-faint">Net Δ</div>
                <div
                  className={`font-mono text-[28px] font-semibold tabular-nums leading-none mt-1 ${
                    report.net_delta > 0 ? 'text-radar-green' : report.net_delta < 0 ? 'text-radar-red' : 'text-paper'
                  }`}
                >
                  {report.net_delta > 0 ? '+' : ''}{fmtInt(report.net_delta)}
                </div>
              </div>
              <div>
                <div className="label text-paper-faint">New</div>
                <div className="font-mono text-[18px] font-semibold tabular-nums text-radar-green leading-none mt-1">
                  {fmtInt(t.new)}
                </div>
              </div>
              <div>
                <div className="label text-paper-faint">Gone</div>
                <div className="font-mono text-[18px] font-semibold tabular-nums text-radar-red leading-none mt-1">
                  {fmtInt(t.gone)}
                </div>
              </div>
            </div>
            <div className="mt-4 text-[11px] font-mono space-y-0.5 text-paper-dim">
              <div>Payment changes: <span className="text-paper">{fmtInt(t.payment_changed)}</span></div>
              <div>Term-end changes: <span className="text-paper">{fmtInt(t.expiry_changed)}</span></div>
              <div>Months-remaining changes: <span className="text-paper">{fmtInt(t.issues_changed)}</span></div>
              {(t.auto_renew_on + t.auto_renew_off > 0) && (
                <div>
                  Auto-renew:
                  <span className="text-amber-glow ml-1">+{fmtInt(t.auto_renew_on)}</span>
                  <span className="text-radar-red ml-2">−{fmtInt(t.auto_renew_off)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="lg:border-l lg:border-ink-500/60 lg:pl-5">
            <div className="label text-paper-trace mb-2">Status transitions</div>
            {transitions.length === 0 ? (
              <div className="font-mono text-[11px] text-paper-trace">none</div>
            ) : (
              <div className="space-y-1 text-[11px] font-mono">
                {transitions.map(([trans, n]) => (
                  <div key={trans} className="grid grid-cols-[auto_1fr] gap-x-3">
                    <span className="tabular-nums text-right text-amber-glow min-w-[36px]">
                      {fmtInt(n)}
                    </span>
                    <span className="text-paper-dim">{trans}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:border-l lg:border-ink-500/60 lg:pl-5">
            <div className="label text-paper-trace mb-2">By publication · new / gone</div>
            <div className="space-y-1 text-[11px] font-mono">
              {Object.entries(report.by_publication).map(([pub, s]) => (
                <div key={pub} className="grid grid-cols-[1fr_auto_auto] gap-x-3">
                  <span className="text-paper truncate">{pub}</span>
                  <span className="text-radar-green tabular-nums text-right min-w-[44px]">
                    +{fmtInt(s.new)}
                  </span>
                  <span className="text-radar-red tabular-nums text-right min-w-[44px]">
                    −{fmtInt(s.gone)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {report.error && (
          <div className="mt-3 text-[11px] font-mono text-radar-red">
            error: {report.error}
          </div>
        )}
      </Panel>
    </section>
  )
}

export function SyncHealthPanel({
  data,
}: {
  data: DashboardSnapshot['sync_health']
}) {
  const accent =
    data.totals.publications_stale_24h > 0 || data.totals.webhook_errors_24h > 0 || data.csv.tone === 'err'
      ? 'red'
      : data.totals.publications_stale_6h > 0 || data.csv.tone === 'warn'
        ? 'amber'
        : 'green'
  return (
    <section>
      <div className="label mb-3">Sync Health · SimpleCirc → AAMCirc</div>
      <Panel accent={accent}>
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-5">
          {/* Per-publication webhook freshness */}
          <div>
            <div className="label text-paper-trace mb-2 flex items-baseline justify-between">
              <span>Webhook freshness · per publication</span>
              <span className="text-paper-trace font-mono text-[10px] normal-case tracking-normal">
                amber / red · source
              </span>
            </div>
            <div className="space-y-1.5">
              {data.by_publication.map((p) => (
                <div
                  key={p.slug}
                  className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-x-3 items-center text-[12px] font-mono"
                >
                  <StatusDot tone={p.tone === 'ok' ? 'ok' : p.tone === 'warn' ? 'warn' : 'err'} pulse={p.tone === 'err'} />
                  <span className="text-paper truncate">{p.name}</span>
                  <span className="text-paper-dim tabular-nums">
                    {p.latest_event_at ? fmtRelative(p.latest_event_at) : 'never'}
                  </span>
                  <span className="text-paper-trace tabular-nums text-right min-w-[54px]">
                    {fmtInt(p.events_24h)} / 24h
                  </span>
                  <span className="text-paper-trace tabular-nums text-right min-w-[110px]">
                    <span className="text-amber-glow/70">{Math.round(p.warn_hours)}h</span>
                    <span className="text-paper-trace"> / </span>
                    <span className="text-radar-red/70">{Math.round(p.err_hours)}h</span>
                    <span
                      className={`ml-1 text-[9px] uppercase ${
                        p.threshold_source === 'autotuned' ? 'text-radar-cyan/70' : 'text-paper-trace'
                      }`}
                      title={p.threshold_source === 'autotuned'
                        ? 'Tuned from last 14 days of webhook cadence'
                        : 'Using global defaults (awaiting enough data for tuning)'}
                    >
                      {p.threshold_source === 'autotuned' ? 'auto' : 'dflt'}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CSV freshness */}
          <div className="lg:border-l lg:border-ink-500/60 lg:pl-5">
            <div className="label text-paper-trace mb-2">CSV reconciliation</div>
            <div className="flex items-center gap-2 mb-1">
              <StatusDot tone={data.csv.tone === 'ok' ? 'ok' : data.csv.tone === 'warn' ? 'warn' : 'err'} pulse={data.csv.tone === 'err'} />
              <span className="font-mono text-[13px] text-paper tabular-nums">
                {data.csv.last_import_at ? fmtRelative(data.csv.last_import_at) : 'never'}
              </span>
            </div>
            <div className="font-mono text-[11px] text-paper-dim">
              {data.csv.last_import_rows > 0
                ? `${fmtInt(data.csv.last_import_rows)} rows imported`
                : 'no imports yet'}
            </div>
            {data.csv.last_import_filename && (
              <div className="font-mono text-[10px] text-paper-trace truncate mt-0.5">
                {data.csv.last_import_filename}
              </div>
            )}
            {data.csv.last_export_at && (
              <div className="mt-3 pt-2 border-t border-ink-500/40">
                <div className="label text-paper-trace mb-0.5">Last auto-export</div>
                <div className="font-mono text-[11px] text-paper-dim tabular-nums">
                  {fmtRelative(data.csv.last_export_at)}
                  <span className="text-paper-trace ml-2">· {data.csv.last_export_status}</span>
                </div>
              </div>
            )}
          </div>

          {/* Error / stale counters */}
          <div className="lg:border-l lg:border-ink-500/60 lg:pl-5 space-y-3">
            <div>
              <div className="label text-paper-trace">Webhook errors · 24h</div>
              <div
                className={`font-mono text-[22px] font-semibold tabular-nums leading-none mt-1 ${
                  data.totals.webhook_errors_24h > 0 ? 'text-radar-red' : 'text-paper-trace'
                }`}
              >
                {fmtInt(data.totals.webhook_errors_24h)}
              </div>
            </div>
            <div>
              <div className="label text-paper-trace">Stale publications</div>
              <div className="font-mono text-[11px] text-paper-dim mt-1">
                <span className={data.totals.publications_stale_6h > 0 ? 'text-amber-glow' : 'text-paper-trace'}>
                  &gt;6h: {data.totals.publications_stale_6h}
                </span>
                <span className="mx-2 text-paper-trace">·</span>
                <span className={data.totals.publications_stale_24h > 0 ? 'text-radar-red' : 'text-paper-trace'}>
                  &gt;24h: {data.totals.publications_stale_24h}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </section>
  )
}

function CompactStat({
  label, value, tone,
}: {
  label: string
  value: string
  tone: string
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="label text-paper-faint truncate">{label}</span>
      <span
        className={`font-mono font-semibold text-[18px] leading-none tabular-nums truncate ${tone}`}
      >
        {value}
      </span>
    </div>
  )
}

type BreakdownRow = {
  label: string
  count: number
  revenue: number
  tone: string
}

function CategoryBreakdownTable({
  rows,
  compact = false,
}: {
  rows: BreakdownRow[]
  compact?: boolean
}) {
  const colWidths = compact
    ? 'grid-cols-[minmax(0,1fr)_44px_56px_52px]'
    : 'grid-cols-[minmax(130px,1fr)_72px_90px_80px]'
  const gap = compact ? 'gap-x-2' : 'gap-x-5'
  const gridTemplate = `grid ${colWidths} ${gap} items-baseline`
  const headerCell = 'label text-paper-trace text-right px-0'
  return (
    <div className="flex flex-col gap-1 text-[11px] font-mono">
      <div className={gridTemplate}>
        <span />
        <span className={headerCell}>count</span>
        <span className={headerCell}>revenue</span>
        <span className={headerCell}>avg</span>
      </div>
      {rows.map((r) => (
        <div key={r.label} className={gridTemplate}>
          <span className="text-paper-dim">{r.label}</span>
          <span className={`${r.tone} tabular-nums text-right`}>{fmtInt(r.count)}</span>
          <span className="text-radar-green tabular-nums text-right">
            {fmtMoney(r.revenue, true)}
          </span>
          <span className="text-radar-cyan/80 tabular-nums text-right">
            {r.count > 0 ? fmtMoney(r.revenue / r.count, false, 2) : '—'}
          </span>
        </div>
      ))}
    </div>
  )
}

function AggregateRow({
  heading,
  subheading,
  activeSubs,
  remitCash,
  autoCount,
  autoRevenue,
  manualEmailCount,
  manualEmailRevenue,
  manualNoEmailCount,
  manualNoEmailRevenue,
  agencyCount,
  agencyRevenue,
  accent = 'amber',
}: {
  heading: string
  subheading: string
  activeSubs: number
  remitCash: number
  autoCount: number
  autoRevenue: number
  manualEmailCount: number
  manualEmailRevenue: number
  manualNoEmailCount: number
  manualNoEmailRevenue: number
  agencyCount: number
  agencyRevenue: number
  accent?: 'amber' | 'green'
}) {
  const avgValue = activeSubs > 0 ? remitCash / activeSubs : 0
  return (
    <Link to="/aggregate" className="block hover:brightness-110 transition-all">
      <Panel accent={accent} className="group cursor-pointer">
        <div className="flex items-stretch gap-6 flex-wrap">
          <div className="flex-1 min-w-[180px] pr-6 border-r border-ink-500/60">
            <div className="label-amber">{heading}</div>
            <div className="font-serif text-[22px] leading-none mt-1 tracking-tight text-paper italic">
              {subheading}
            </div>
            <div className="label mt-1 text-paper-trace">drill down →</div>
          </div>
          <div className="flex gap-8 items-baseline flex-1 flex-wrap">
            <Metric label="Active subs" value={activeSubs} tone="amber" size="md" />
            <Metric
              label="Remit · cash"
              value={fmtMoney(remitCash, true, 2)}
              tone="green"
              size="md"
            />
            <Metric
              label="Avg · sub / yr"
              value={fmtMoney(avgValue, false, 2)}
              tone="cyan"
              size="md"
            />
            <div className="self-center border-l border-ink-500/40 pl-5 min-w-[400px]">
              <CategoryBreakdownTable
                rows={[
                  { label: 'Auto-renew', count: autoCount, revenue: autoRevenue, tone: 'text-amber-glow' },
                  { label: 'Manual · email', count: manualEmailCount, revenue: manualEmailRevenue, tone: 'text-radar-cyan' },
                  { label: 'Manual · no email', count: manualNoEmailCount, revenue: manualNoEmailRevenue, tone: 'text-radar-violet' },
                  { label: 'Agency', count: agencyCount, revenue: agencyRevenue, tone: 'text-radar-red' },
                ]}
              />
            </div>
          </div>
        </div>
      </Panel>
    </Link>
  )
}

function AggregateStatusRow({ data }: { data: DashboardSnapshot }) {
  const t = data.totals
  return (
    <AggregateRow
      heading="ALL TITLES · AGGREGATE"
      subheading="Current circulation rollup"
      activeSubs={t.active}
      remitCash={t.revenue_active_remit}
      autoCount={t.current_auto_count}
      autoRevenue={t.current_auto_revenue}
      manualEmailCount={t.current_manual_with_email_count}
      manualEmailRevenue={t.current_manual_with_email_revenue}
      manualNoEmailCount={t.current_manual_no_email_count}
      manualNoEmailRevenue={t.current_manual_no_email_revenue}
      agencyCount={t.current_agency_count}
      agencyRevenue={t.current_agency_remit_revenue}
      accent="amber"
    />
  )
}

function AggregateForecastRow({ data }: { data: DashboardSnapshot }) {
  const totals = data.publications.reduce(
    (acc, p) => {
      const f = p.forecast_12mo
      acc.count += f.count
      acc.remit += f.remit
      acc.auto += f.auto_count
      acc.autoRev += f.auto_revenue
      acc.manualEmail += f.manual_with_email_count
      acc.manualEmailRev += f.manual_with_email_revenue
      acc.manualNoEmail += f.manual_no_email_count
      acc.manualNoEmailRev += f.manual_no_email_revenue
      acc.agency += f.agency_count
      acc.agencyRev += f.agency_remit_revenue
      return acc
    },
    {
      count: 0, remit: 0,
      auto: 0, autoRev: 0,
      manualEmail: 0, manualEmailRev: 0,
      manualNoEmail: 0, manualNoEmailRev: 0,
      agency: 0, agencyRev: 0,
    },
  )
  return (
    <AggregateRow
      heading="ALL TITLES · 12-MO FORECAST"
      subheading="Rolling 12-month outlook"
      activeSubs={totals.count}
      remitCash={totals.remit}
      autoCount={totals.auto}
      autoRevenue={totals.autoRev}
      manualEmailCount={totals.manualEmail}
      manualEmailRevenue={totals.manualEmailRev}
      manualNoEmailCount={totals.manualNoEmail}
      manualNoEmailRevenue={totals.manualNoEmailRev}
      agencyCount={totals.agency}
      agencyRevenue={totals.agencyRev}
      accent="green"
    />
  )
}

function Forecast12moCard({
  pub, loading,
}: {
  pub: DashboardSnapshot['publications'][number] | null
  loading: boolean
}) {
  if (!pub) {
    return (
      <div className="border border-ink-500 bg-ink-800/60 h-[200px] flex items-center justify-center">
        <span className="label text-paper-trace">{loading ? 'LOADING…' : '—'}</span>
      </div>
    )
  }
  const color = publicationColor(pub.slug)
  const f = pub.forecast_12mo
  return (
    <Link to={`/titles/${pub.slug}`} className="block hover:brightness-110 transition-all">
      <Panel accent="none" className="group cursor-pointer">
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="label" style={{ color }}>{pub.slug.replace(/_/g, ' ')}</div>
              <div className="font-serif text-[18px] leading-none mt-1 tracking-tight text-paper">
                {pub.name}
              </div>
            </div>
            <PubBadge slug={pub.slug} compact />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <CompactStat label="Subs" value={fmtInt(f.count)} tone="text-amber-glow" />
            <CompactStat label="Remit" value={fmtMoney(f.remit, true)} tone="text-radar-green" />
            <CompactStat
              label="Avg / yr"
              value={f.count > 0 ? fmtMoney(f.remit / f.count, false, 2) : '—'}
              tone="text-radar-cyan"
            />
          </div>

          <CategoryBreakdownTable
            compact
            rows={[
              { label: 'Auto-renew', count: f.auto_count, revenue: f.auto_revenue, tone: 'text-amber-glow' },
              { label: 'Manual · email', count: f.manual_with_email_count, revenue: f.manual_with_email_revenue, tone: 'text-radar-cyan' },
              { label: 'Manual · no email', count: f.manual_no_email_count, revenue: f.manual_no_email_revenue, tone: 'text-radar-violet' },
              { label: 'Agency', count: f.agency_count, revenue: f.agency_remit_revenue, tone: 'text-radar-red' },
            ]}
          />

          <div className="text-right pt-1">
            <span className="label-amber group-hover:underline">Drill down →</span>
          </div>
        </div>
      </Panel>
    </Link>
  )
}

function ReachabilityMatrix({ data }: { data: DashboardSnapshot['reachability'] }) {
  const rows = [
    {
      slug: '__all__',
      name: 'All Titles',
      ...data.totals,
      total_active: data.totals.total_active,
      isAgg: true,
    },
    ...data.by_publication.map((p) => ({ ...p, isAgg: false })),
  ]

  const pct = (n: number, total: number) =>
    total > 0 ? `${((n / total) * 100).toFixed(0)}%` : '—'

  return (
    <Panel accent="amber" bodyClassName="p-0">
      <table className="w-full">
        <thead>
          <tr className="border-b border-ink-500">
            <Th></Th>
            <ThGroup className="border-l border-ink-500">AUTO RENEW</ThGroup>
            <ThGroup className="border-l border-ink-500">MANUAL</ThGroup>
            <Th align="right" className="border-l border-ink-500">Total</Th>
          </tr>
          <tr className="border-b border-ink-500">
            <Th>Publication</Th>
            <Th align="right" className="border-l border-ink-500">Email</Th>
            <Th align="right">No Email</Th>
            <Th align="right" className="border-l border-ink-500">Email</Th>
            <Th align="right">No Email</Th>
            <Th align="right" className="border-l border-ink-500">Active</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.slug}
              className={`border-b border-ink-500/40 hover:bg-ink-700/40 ${
                row.isAgg ? 'bg-ink-700/60 font-semibold' : ''
              }`}
            >
              <td className="px-4 py-3">
                {row.isAgg ? (
                  <span className="label-amber">{row.name}</span>
                ) : (
                  <PubBadge slug={row.slug} />
                )}
              </td>
              <MatrixCell n={row.auto_email} total={row.total_active} tone="green" />
              <MatrixCell n={row.auto_no_email} total={row.total_active} tone="amber" />
              <MatrixCell n={row.manual_email} total={row.total_active} tone="cyan" className="border-l border-ink-500" />
              <MatrixCell n={row.manual_no_email} total={row.total_active} tone="violet" />
              <td className="px-4 py-3 text-right border-l border-ink-500">
                <span className={`font-mono font-semibold text-[15px] tabular-nums ${row.isAgg ? 'text-amber-glow' : 'text-paper'}`}>
                  {fmtInt(row.total_active)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6} className="px-4 py-2 border-t border-ink-500/40">
              <div className="flex flex-wrap items-center gap-4 text-[11px] font-mono">
                <span className="label text-paper-trace">CHANNEL TOTALS</span>
                <span className="text-paper-dim">
                  no email total: <span className="text-radar-violet">{fmtInt(data.totals.total_no_email)}</span>
                  <span className="text-paper-trace"> ({pct(data.totals.total_no_email, data.totals.total_active)})</span>
                </span>
                <span className="text-paper-trace">·</span>
                <span className="text-paper-dim">
                  on auto-renew: <span className="text-amber-glow">{fmtInt(data.totals.total_auto_renew)}</span>
                  <span className="text-paper-trace"> ({pct(data.totals.total_auto_renew, data.totals.total_active)})</span>
                </span>
                <span className="text-paper-trace">·</span>
                <span className="text-paper-dim">
                  manual renewal: <span className="text-radar-cyan">{fmtInt(data.totals.total_manual)}</span>
                  <span className="text-paper-trace"> ({pct(data.totals.total_manual, data.totals.total_active)})</span>
                </span>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </Panel>
  )
}

function MatrixCell({
  n, total, tone, className = '',
}: {
  n: number
  total: number
  tone: 'amber' | 'green' | 'cyan' | 'violet'
  className?: string
}) {
  const pctStr = total > 0 ? `${((n / total) * 100).toFixed(0)}%` : ''
  const toneClass = {
    amber: 'text-amber-glow',
    green: 'text-radar-green',
    cyan: 'text-radar-cyan',
    violet: 'text-radar-violet',
  }[tone]
  return (
    <td className={`px-4 py-3 text-right ${className}`}>
      <div className={`font-mono tabular-nums ${n === 0 ? 'text-paper-trace' : toneClass}`}>
        {fmtInt(n)}
      </div>
      {n > 0 && (
        <div className="font-mono text-[10px] text-paper-trace tabular-nums">
          {pctStr}
        </div>
      )}
    </td>
  )
}

function ThGroup({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      colSpan={2}
      className={`label py-2 px-4 font-normal text-center text-amber-glow/80 ${className}`}
    >
      {children}
    </th>
  )
}

// Color each payment-type column with a stable tone so rows are scannable.
const PAYMENT_TONES: Record<string, string> = {
  'Check': 'text-radar-cyan',
  'Auto Renewal': 'text-amber-glow',
  'Credit Card': 'text-radar-green',
  'PayPal Button': 'text-radar-violet',
  'Invoice': 'text-radar-red',
  'Other': 'text-paper',
  'Agency': 'text-amber-glow',
  'Gift (by giver)': 'text-radar-violet',
  'Comp': 'text-paper-faint',
  '(none on file)': 'text-paper-faint',
}

function PaymentMethodMatrix({
  data,
}: {
  data: DashboardSnapshot['payment_methods']
}) {
  const rows: Array<{
    slug: string
    name: string
    counts: Record<string, number>
    total: number
    isAgg: boolean
  }> = [
    { slug: '__all__', name: 'All Titles', counts: data.totals, total: data.grand_total, isAgg: true },
    ...data.by_publication.map((p) => ({ ...p, isAgg: false })),
  ]

  const pct = (n: number, total: number) =>
    total > 0 ? `${((n / total) * 100).toFixed(0)}%` : ''

  return (
    <Panel accent="cyan" bodyClassName="p-0">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-ink-500">
              <th className="label py-2 px-4 font-normal text-left">Publication</th>
              {data.buckets.map((b) => (
                <th
                  key={b}
                  className={`label py-2 px-4 font-normal text-right border-l border-ink-500 ${PAYMENT_TONES[b] ?? 'text-paper-dim'}`}
                >
                  {b}
                </th>
              ))}
              <th className="label py-2 px-4 font-normal text-right border-l border-ink-500">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.slug}
                className={`border-b border-ink-500/40 hover:bg-ink-700/40 ${
                  row.isAgg ? 'bg-ink-700/60 font-semibold' : ''
                }`}
              >
                <td className="px-4 py-3">
                  {row.isAgg ? (
                    <span className="label-amber">{row.name}</span>
                  ) : (
                    <PubBadge slug={row.slug} />
                  )}
                </td>
                {data.buckets.map((b) => {
                  const n = row.counts[b] ?? 0
                  const tone = PAYMENT_TONES[b] ?? 'text-paper'
                  return (
                    <td key={b} className="px-4 py-3 text-right border-l border-ink-500">
                      <div className={`font-mono tabular-nums ${n === 0 ? 'text-paper-trace' : tone}`}>
                        {fmtInt(n)}
                      </div>
                      {n > 0 && (
                        <div className="font-mono text-[10px] text-paper-trace tabular-nums">
                          {pct(n, row.total)}
                        </div>
                      )}
                    </td>
                  )
                })}
                <td className="px-4 py-3 text-right border-l border-ink-500">
                  <span
                    className={`font-mono font-semibold text-[15px] tabular-nums ${row.isAgg ? 'text-amber-glow' : 'text-paper'}`}
                  >
                    {fmtInt(row.total)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

type CohortCellVariant = 'primary' | 'muted' | 'ar-on'

type ExpirationPair = {
  count: number
  revenue: number
  renewed: { count: number; revenue: number }
}

/**
 * Renders a cohort as a PAIR of cells: a "total" cell (count + revenue)
 * and a "renewed" cell (count + revenue + % of total). Border styling
 * matches the cohort group so the two sub-cells visually group
 * together.
 */
function CohortCell({
  pair,
  variant,
  border = false,
  large = false,
}: {
  pair: ExpirationPair
  variant: CohortCellVariant
  border?: boolean
  large?: boolean
}) {
  const isZero = pair.count === 0
  const groupBorder = (() => {
    if (variant === 'ar-on') return 'border-l-2 border-amber-glow/40 bg-amber-glow/5'
    if (border) return 'border-l border-ink-500/40'
    return ''
  })()
  const countToneClass = (() => {
    if (variant === 'ar-on') return 'text-amber-glow font-semibold'
    if (variant === 'primary') return 'text-paper font-semibold'
    return isZero ? 'text-paper-trace' : 'text-paper-dim'
  })()
  const countSize = large ? (variant === 'primary' || variant === 'ar-on' ? 'text-[16px]' : 'text-[15px]') : (variant === 'primary' || variant === 'ar-on' ? 'text-[15px]' : 'text-[14px]')
  const renewed = pair.renewed
  const pct = pair.count > 0 ? (renewed.count / pair.count) * 100 : 0
  const renewedHasData = renewed.count > 0
  const renewedBgClass = variant === 'ar-on' ? 'bg-amber-glow/5' : ''
  return (
    <>
      <td className={`px-3 py-2 text-right align-top ${groupBorder}`}>
        <div className={`font-mono tabular-nums ${countSize} ${countToneClass}`}>
          {fmtInt(pair.count)}
        </div>
        {pair.revenue > 0 && (
          <div className="font-mono text-[10px] tabular-nums text-radar-green/80">
            {fmtMoney(pair.revenue, true)}
          </div>
        )}
      </td>
      <td className={`px-3 py-2 text-right align-top border-l border-dotted border-ink-500/40 ${renewedBgClass}`}>
        {renewedHasData ? (
          <>
            <div className="font-mono tabular-nums text-radar-green text-[14px] font-semibold">
              {fmtInt(renewed.count)}
            </div>
            {renewed.revenue > 0 && (
              <div className="font-mono text-[10px] tabular-nums text-radar-green/70">
                {fmtMoney(renewed.revenue, true)}
              </div>
            )}
            <div className="font-mono text-[10px] tabular-nums text-paper-trace mt-0.5">
              {pct.toFixed(1)}%
            </div>
          </>
        ) : (
          <div className="font-mono tabular-nums text-paper-trace text-[14px]">—</div>
        )}
      </td>
    </>
  )
}

/**
 * Two-row header pair for a cohort: the cohort name spans both
 * sub-columns (Total / Renewed).
 */
function CohortHeader({
  label,
  variant,
  border = false,
  subtitle,
}: {
  label: string
  variant: CohortCellVariant
  border?: boolean
  subtitle?: string
}) {
  const groupBorder = (() => {
    if (variant === 'ar-on') return 'border-l-2 border-amber-glow/40 bg-amber-glow/10'
    if (border) return 'border-l border-ink-500/40'
    return ''
  })()
  const labelTone = variant === 'ar-on' ? 'text-amber-glow' : ''
  return (
    <th colSpan={2} className={`label py-2 px-3 font-normal text-center ${groupBorder} ${labelTone}`}>
      {label}
      {subtitle && (
        <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
          {subtitle}
        </div>
      )}
    </th>
  )
}

function CohortSubHeader({
  variant,
  border = false,
}: {
  variant: CohortCellVariant
  border?: boolean
}) {
  const groupBorder = (() => {
    if (variant === 'ar-on') return 'border-l-2 border-amber-glow/40 bg-amber-glow/10'
    if (border) return 'border-l border-ink-500/40'
    return ''
  })()
  const renewedBg = variant === 'ar-on' ? 'bg-amber-glow/10' : ''
  return (
    <>
      <th className={`label py-1 px-3 font-normal text-right text-paper-trace ${groupBorder}`}>
        Total
      </th>
      <th className={`label py-1 px-3 font-normal text-right text-radar-green border-l border-dotted border-ink-500/40 ${renewedBg}`}>
        Renewed
      </th>
    </>
  )
}

function UpcomingExpirations({
  data,
}: {
  data: DashboardSnapshot['upcoming_expirations']
}) {
  const slugs = data.publications.map((p) => p.slug)

  // Per-(month, slug) counts + revenue + renewed mapped to the
  // 5-column layout used by the Mailing Pull tile. Newly Expired =
  // sum of all four cohort sub-buckets.
  type RowCells = {
    newly_expired: ExpirationPair
    agent: ExpirationPair
    ar_on: ExpirationPair
    ar_off_email: ExpirationPair
    ar_off_no_email: ExpirationPair
  }
  const emptyPair: ExpirationPair = {
    count: 0, revenue: 0, renewed: { count: 0, revenue: 0 },
  }
  const sumPair = (...xs: ExpirationPair[]): ExpirationPair => ({
    count: xs.reduce((s, x) => s + x.count, 0),
    revenue: xs.reduce((s, x) => s + x.revenue, 0),
    renewed: {
      count: xs.reduce((s, x) => s + (x.renewed?.count ?? 0), 0),
      revenue: xs.reduce((s, x) => s + (x.renewed?.revenue ?? 0), 0),
    },
  })
  const cellFor = (
    monthRow: DashboardSnapshot['upcoming_expirations']['by_month'][number],
    slug: string,
  ): RowCells => {
    const c = monthRow.by_pub[slug]
    const ar = c?.ar ?? emptyPair
    const agent = c?.agent ?? emptyPair
    const ar_off_email = c?.no_ar_email ?? emptyPair
    const ar_off_no_email = c?.no_ar_no_email ?? emptyPair
    return {
      newly_expired: sumPair(ar, agent, ar_off_email, ar_off_no_email),
      agent,
      ar_on: ar,
      ar_off_email,
      ar_off_no_email,
    }
  }
  const monthTotals = (
    monthRow: DashboardSnapshot['upcoming_expirations']['by_month'][number],
  ): RowCells => {
    const ar = monthRow.totals_by_cohort.ar
    const agent = monthRow.totals_by_cohort.agent
    const ar_off_email = monthRow.totals_by_cohort.no_ar_email
    const ar_off_no_email = monthRow.totals_by_cohort.no_ar_no_email
    return {
      newly_expired: sumPair(ar, agent, ar_off_email, ar_off_no_email),
      agent,
      ar_on: ar,
      ar_off_email,
      ar_off_no_email,
    }
  }

  // The cellFor/monthTotals helpers above are typed against by_month
  // entries; prior_cohorts has an identical shape so the same helpers
  // work after a structural cast.
  type MonthRow = DashboardSnapshot['upcoming_expirations']['by_month'][number]

  return (
    <Panel accent="green" bodyClassName="p-0">
      <div className="px-4 pt-3 pb-2 text-[12px] text-paper-dim font-mono leading-relaxed">
        Forecast bucketed by <code>issues_remaining</code> — month N
        means subs that will hit zero on the (current month + N)
        mailing-label pull. Same five-column layout as the Mailing
        Pull tile so today's cohort flows naturally into next month's
        forecast: Newly Expired = sum of the four mutually-exclusive
        buckets (Agent / AR On / AR Off Email / AR Off No Email).
        Current-month row also includes legacy lapsed subs whose
        issues_remaining has sat at zero in prior cycles — interpret
        with care.
      </div>

      {/* Prior cohorts — frozen historical cohorts whose Renewed cells
          keep advancing as ops's email and direct-mail efforts pay
          off through the year. */}
      {data.prior_cohorts.length > 0 && (
        <div className="border-t-2 border-radar-green/30 bg-ink-700/40">
          <div className="px-4 pt-3 pb-1 flex items-baseline gap-3">
            <span className="label-amber" style={{ color: 'var(--radar-green, #34d399)' }}>
              Prior Cohorts · Renewal Progress
            </span>
            <span className="font-mono text-[10px] text-paper-trace italic">
              renewal % keeps growing through the year as members renew
            </span>
          </div>
          {data.prior_cohorts.map((row) => {
            const monthRow = row as unknown as MonthRow
            const tot = monthTotals(monthRow)
            return (
              <div key={`prior-${row.month}`} className="border-t border-ink-500/40">
                <div className="px-4 pt-3 pb-1">
                  <span className="label">{row.month}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="border-b border-ink-500/40">
                        <th rowSpan={2} className="label py-2 px-3 font-normal text-left align-bottom">Title</th>
                        <CohortHeader label="Newly Expired" variant="primary" />
                        <CohortHeader label="Agent" variant="muted" border />
                        <CohortHeader label="AR On" variant="ar-on" />
                        <CohortHeader label="AR Off Email" variant="muted" border />
                        <CohortHeader label="AR Off No Email" variant="muted" border />
                      </tr>
                      <tr className="border-b border-ink-500/40">
                        <CohortSubHeader variant="primary" />
                        <CohortSubHeader variant="muted" border />
                        <CohortSubHeader variant="ar-on" />
                        <CohortSubHeader variant="muted" border />
                        <CohortSubHeader variant="muted" border />
                      </tr>
                    </thead>
                    <tbody>
                      {slugs.map((slug) => {
                        const cell = cellFor(monthRow, slug)
                        return (
                          <tr key={slug} className="border-b border-ink-500/40 hover:bg-ink-700/30">
                            <td
                              className="px-3 py-2 font-mono text-[11px] uppercase tracking-label"
                              style={{ color: publicationColor(slug) }}
                            >
                              {slug.replace(/_/g, ' ')}
                            </td>
                            <CohortCell pair={cell.newly_expired} variant="primary" />
                            <CohortCell pair={cell.agent} variant="muted" border />
                            <CohortCell pair={cell.ar_on} variant="ar-on" />
                            <CohortCell pair={cell.ar_off_email} variant="muted" border />
                            <CohortCell pair={cell.ar_off_no_email} variant="muted" border />
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-ink-500 bg-ink-700/40">
                        <td className="px-3 py-2 label">TOTAL</td>
                        <CohortCell pair={tot.newly_expired} variant="primary" />
                        <CohortCell pair={tot.agent} variant="muted" border />
                        <CohortCell pair={tot.ar_on} variant="ar-on" />
                        <CohortCell pair={tot.ar_off_email} variant="muted" border />
                        <CohortCell pair={tot.ar_off_no_email} variant="muted" border />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {data.by_month.map((row, idx) => {
        const isCurrent = idx === 0
        const tot = monthTotals(row)
        return (
          <div key={row.month} className="border-t border-ink-500/40">
            <div className="px-4 pt-3 pb-1 flex items-baseline gap-3">
              <span className="label">{row.month}</span>
              {isCurrent && (
                <span className="font-mono text-[10px] text-paper-trace italic">
                  (incl. legacy at-zero)
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-ink-500/40">
                    <th rowSpan={2} className="label py-2 px-3 font-normal text-left align-bottom">Title</th>
                    <CohortHeader label="Newly Expired" variant="primary" />
                    <CohortHeader label="Agent" variant="muted" border />
                    <CohortHeader label="AR On" variant="ar-on" />
                    <CohortHeader label="AR Off Email" variant="muted" border />
                    <CohortHeader label="AR Off No Email" variant="muted" border />
                  </tr>
                  <tr className="border-b border-ink-500/40">
                    <CohortSubHeader variant="primary" />
                    <CohortSubHeader variant="muted" border />
                    <CohortSubHeader variant="ar-on" />
                    <CohortSubHeader variant="muted" border />
                    <CohortSubHeader variant="muted" border />
                  </tr>
                </thead>
                <tbody>
                  {slugs.map((slug) => {
                    const cell = cellFor(row, slug)
                    return (
                      <tr key={slug} className="border-b border-ink-500/40 hover:bg-ink-700/30">
                        <td
                          className="px-3 py-2 font-mono text-[11px] uppercase tracking-label"
                          style={{ color: publicationColor(slug) }}
                        >
                          {slug.replace(/_/g, ' ')}
                        </td>
                        <CohortCell pair={cell.newly_expired} variant="primary" />
                        <CohortCell pair={cell.agent} variant="muted" border />
                        <CohortCell pair={cell.ar_on} variant="ar-on" />
                        <CohortCell pair={cell.ar_off_email} variant="muted" border />
                        <CohortCell pair={cell.ar_off_no_email} variant="muted" border />
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-ink-500 bg-ink-700/40">
                    <td className="px-3 py-2 label">TOTAL</td>
                    <CohortCell pair={tot.newly_expired} variant="primary" />
                    <CohortCell pair={tot.agent} variant="muted" border />
                    <CohortCell pair={tot.ar_on} variant="ar-on" />
                    <CohortCell pair={tot.ar_off_email} variant="muted" border />
                    <CohortCell pair={tot.ar_off_no_email} variant="muted" border />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      })}

      {/* 12-month rollup — same layout summed across all months. */}
      <div className="border-t-2 border-amber-glow/30 bg-ink-700/60">
        <div className="px-4 pt-3 pb-1 label-amber">12-Month Rollup</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-ink-500/40">
                <th rowSpan={2} className="label py-2 px-3 font-normal text-left align-bottom">Title</th>
                <CohortHeader label="Newly Expired" variant="primary" />
                <CohortHeader label="Agent" variant="muted" border />
                <CohortHeader label="AR On" variant="ar-on" />
                <CohortHeader label="AR Off Email" variant="muted" border />
                <CohortHeader label="AR Off No Email" variant="muted" border />
              </tr>
              <tr className="border-b border-ink-500/40">
                <CohortSubHeader variant="primary" />
                <CohortSubHeader variant="muted" border />
                <CohortSubHeader variant="ar-on" />
                <CohortSubHeader variant="muted" border />
                <CohortSubHeader variant="muted" border />
              </tr>
            </thead>
            <tbody>
              {slugs.map((slug) => {
                const t = data.totals_by_pub[slug]?.by_cohort
                const ar = t?.ar ?? { count: 0, revenue: 0 }
                const agent = t?.agent ?? { count: 0, revenue: 0 }
                const ar_off_email = t?.no_ar_email ?? { count: 0, revenue: 0 }
                const ar_off_no_email = t?.no_ar_no_email ?? { count: 0, revenue: 0 }
                const newly = sumPair(ar, agent, ar_off_email, ar_off_no_email)
                return (
                  <tr key={slug} className="border-b border-ink-500/40">
                    <td
                      className="px-3 py-2 font-mono text-[11px] uppercase tracking-label"
                      style={{ color: publicationColor(slug) }}
                    >
                      {slug.replace(/_/g, ' ')}
                    </td>
                    <CohortCell pair={newly} variant="primary" />
                    <CohortCell pair={agent} variant="muted" border />
                    <CohortCell pair={ar} variant="ar-on" />
                    <CohortCell pair={ar_off_email} variant="muted" border />
                    <CohortCell pair={ar_off_no_email} variant="muted" border />
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-amber-glow/40 bg-ink-700/80">
                <td className="px-3 py-2.5 label-amber">12-MO TOTAL</td>
                {(() => {
                  const grandNewly = sumPair(
                    data.grand_by_cohort.ar,
                    data.grand_by_cohort.agent,
                    data.grand_by_cohort.no_ar_email,
                    data.grand_by_cohort.no_ar_no_email,
                  )
                  return (
                    <>
                      <CohortCell pair={grandNewly} variant="primary" large />
                      <CohortCell pair={data.grand_by_cohort.agent} variant="muted" border large />
                      <CohortCell pair={data.grand_by_cohort.ar} variant="ar-on" large />
                      <CohortCell pair={data.grand_by_cohort.no_ar_email} variant="muted" border large />
                      <CohortCell pair={data.grand_by_cohort.no_ar_no_email} variant="muted" border large />
                    </>
                  )
                })()}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Panel>
  )
}

type FunnelBucket = 'renewed' | 'in_retry' | 'terminal_fail' | 'ar_disabled' | 'awaiting'
// Display order — left-to-right tracks the cycle funnel: success
// first, then in-flight failure states, then idle.
const AR_STATUS_ORDER: FunnelBucket[] = ['renewed', 'in_retry', 'terminal_fail', 'ar_disabled', 'awaiting']

const AR_STATUS_LABEL: Record<FunnelBucket, string> = {
  renewed: 'Renewed',
  in_retry: 'In Retry',
  terminal_fail: 'Terminal Failure',
  ar_disabled: 'AR Disabled',
  awaiting: 'Awaiting',
}
const AR_STATUS_TONE: Record<FunnelBucket, string> = {
  renewed: 'text-radar-green',
  in_retry: 'text-amber-glow',
  terminal_fail: 'text-red-400',
  ar_disabled: 'text-paper-trace',
  awaiting: 'text-paper-dim',
}
const AR_STATUS_NOTE: Record<FunnelBucket, string> = {
  renewed: 'charge succeeded this cycle',
  in_retry: 'attempted, failed, retry budget left',
  terminal_fail: 'retries exhausted (FIFTH/FINAL)',
  ar_disabled: 'AR turned off mid-cycle',
  awaiting: 'no SC activity this cycle yet',
}

/**
 * Tone mapping for raw SC AR-status values shown in the cycle-funnel
 * stages. CHARGED is success-green, *FAILED variants are amber/red,
 * CARD UPDATED is informational, AR-off is a soft "exit funnel" tone.
 */
const RAW_AR_TONE: Record<string, string> = {
  'CHARGED': 'text-radar-green',
  'CARD UPDATED': 'text-cyan-400',
  'FIRST CHARGE FAILED': 'text-amber-glow',
  'CHARGE FAILED': 'text-amber-glow',
  'RECHARGE FAILED': 'text-amber-glow',
  'SECOND CHARGE FAILED': 'text-amber-glow',
  'THIRD CHARGE FAILED': 'text-amber-glow/80',
  'FOURTH CHARGE FAILED': 'text-amber-glow/80',
  'FIFTH CHARGE FAILED': 'text-red-400',
  'FINAL CHARGE FAILED': 'text-red-400',
  'AUTO RENEW TURNED OFF': 'text-paper-trace',
  'CHARGE - AUTO RENEW OFF FOR PUBLICATION': 'text-paper-trace',
}

function ARCycleEffectiveness({
  data,
}: {
  data: DashboardSnapshot['ar_cycle_effectiveness']
}) {
  return (
    <Panel accent="green" bodyClassName="p-0">
      <div className="px-4 pt-3 pb-2 text-[12px] text-paper-dim font-mono leading-relaxed">
        Walks SC's retry pipeline stage by stage for each cohort. Each
        attempt sub-table shows where subs landed on their Nth charge
        attempt — successes exit the funnel as <span className="text-radar-green">Renewed</span>;
        failures carry forward to the next attempt. SC retries up to
        five times before terminal failure. KPIs at the bottom of each
        cohort summarize first-attempt success vs eventual conversion.
      </div>
      {data.cohorts.map((cohort) => (
        <ARCycleEffectivenessCohort key={cohort.cohort_month} cohort={cohort} />
      ))}
    </Panel>
  )
}

function ARCycleEffectivenessCohort({
  cohort,
}: {
  cohort: DashboardSnapshot['ar_cycle_effectiveness']['cohorts'][number]
}) {
  const dispLabels: Array<{
    key: keyof typeof cohort.final_disposition
    label: string
    tone: string
    note: string
  }> = [
    { key: 'renewed', label: 'Renewed via AR', tone: 'text-radar-green', note: 'charge succeeded' },
    { key: 'terminal_failed', label: 'Terminal Failure', tone: 'text-red-400', note: 'retry budget hit' },
    { key: 'ar_disabled', label: 'AR Turned Off', tone: 'text-paper-trace', note: 'mid-cycle disable' },
    { key: 'card_update_pending', label: 'Card Update Pending', tone: 'text-cyan-400', note: 'will retry next cycle' },
    { key: 'still_in_cycle', label: 'Still In Cycle', tone: 'text-paper-dim', note: 'queued / between retries' },
  ]
  return (
    <div className="border-t border-ink-500/40">
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-baseline gap-3">
        <span className="label text-radar-green">{cohort.cohort_month} Cohort</span>
        <span className="font-mono text-[11px] text-paper-dim">
          {fmtInt(cohort.cohort_size)} AR On members ·{' '}
          <span className="text-radar-green/80">{fmtMoney(cohort.cohort_revenue, false)}</span>{' '}
          potential
        </span>
      </div>

      {/* Stages — one mini-table per attempt sequence number. */}
      <div className="px-4 pb-3 space-y-2">
        {cohort.stages.length === 0 ? (
          <div className="font-mono text-[11px] text-paper-trace italic px-1 py-2">
            No SC attempts recorded yet for this cohort.
          </div>
        ) : (
          cohort.stages.map((stage) => {
            const chargedPair = stage.outcomes['CHARGED'] ?? { count: 0, revenue: 0 }
            const cardPair = stage.outcomes['CARD UPDATED'] ?? { count: 0, revenue: 0 }
            const failedCount = stage.entered - chargedPair.count - cardPair.count
            const cohortPct = cohort.cohort_size > 0
              ? (stage.entered / cohort.cohort_size) * 100 : 0
            return (
              <div
                key={stage.attempt_seq}
                className="border border-ink-500/40 rounded-sm bg-ink-700/20"
              >
                <div className="px-3 py-1.5 border-b border-ink-500/40 flex items-baseline gap-3 bg-ink-700/40">
                  <span className="font-mono text-[11px] uppercase tracking-label text-paper">
                    Attempt {stage.attempt_seq}
                  </span>
                  <span className="font-mono text-[10px] text-paper-trace">
                    {fmtInt(stage.entered)} subs ({cohortPct.toFixed(1)}% of cohort) ·{' '}
                    <span className="text-radar-green/80">{fmtMoney(stage.entered_revenue, false)}</span> · charged {fmtInt(chargedPair.count)} · failed {fmtInt(failedCount)}
                    {cardPair.count > 0 && ` · card updated ${fmtInt(cardPair.count)}`}
                  </span>
                </div>
                <div className="px-3 py-1.5 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                  {Object.entries(stage.outcomes)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([status, pair]) => {
                      const pct = stage.entered > 0
                        ? (pair.count / stage.entered) * 100 : 0
                      const tone = RAW_AR_TONE[status] ?? 'text-paper-dim'
                      return (
                        <div key={status} className="flex items-baseline gap-2 flex-wrap">
                          <span className={`font-mono text-[11px] ${tone}`}>
                            {status}
                          </span>
                          <span className="font-mono tabular-nums text-paper">
                            {fmtInt(pair.count)}
                          </span>
                          <span className="font-mono text-[10px] tabular-nums text-radar-green/80">
                            {fmtMoney(pair.revenue, true)}
                          </span>
                          <span className="font-mono text-[10px] text-paper-trace tabular-nums">
                            ({pct.toFixed(1)}%)
                          </span>
                        </div>
                      )
                    })}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Final disposition. */}
      <div className="border-t border-ink-500/40 px-4 py-3">
        <div className="label mb-2">Final Disposition</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {dispLabels.map(({ key, label, tone, note }) => {
            const cell = cohort.final_disposition[key] ?? { count: 0, revenue: 0 }
            const pct = cohort.cohort_size > 0
              ? (cell.count / cohort.cohort_size) * 100 : 0
            return (
              <div key={key} className="border border-ink-500/40 rounded-sm px-3 py-2 bg-ink-700/20">
                <div className={`font-mono text-[10px] uppercase tracking-label ${tone}`}>
                  {label}
                </div>
                <div className={`font-mono font-semibold tabular-nums text-[18px] mt-0.5 ${tone}`}>
                  {fmtInt(cell.count)}
                </div>
                {cell.revenue > 0 && (
                  <div className="font-mono text-[10px] tabular-nums text-radar-green/80">
                    {fmtMoney(cell.revenue, true)}
                  </div>
                )}
                <div className="font-mono text-[10px] tabular-nums text-paper-dim">
                  {pct.toFixed(1)}% · {note}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* KPIs strip. */}
      <div className="border-t border-ink-500/40 px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="label">First-Attempt Success</div>
          <div className="font-mono font-semibold text-radar-green text-[20px] tabular-nums">
            {cohort.kpis.first_attempt_success_pct.toFixed(1)}%
          </div>
          <div className="font-mono text-[10px] text-paper-dim">
            charged on attempt 1 / cohort size
          </div>
        </div>
        <div>
          <div className="label">Retry Recovery</div>
          <div className="font-mono font-semibold text-amber-glow text-[20px] tabular-nums">
            {cohort.kpis.retry_recovery_pct.toFixed(1)}%
          </div>
          <div className="font-mono text-[10px] text-paper-dim">
            of attempt-1 failures eventually charged
          </div>
        </div>
        <div>
          <div className="label">Total Conversion</div>
          <div className="font-mono font-semibold text-radar-green text-[20px] tabular-nums">
            {cohort.kpis.conversion_pct.toFixed(1)}%
          </div>
          <div className="font-mono text-[10px] text-paper-dim">
            renewed / cohort size
          </div>
        </div>
        <div>
          <div className="label">Terminal Loss</div>
          <div className="font-mono font-semibold text-red-400 text-[20px] tabular-nums">
            {cohort.kpis.terminal_loss_pct.toFixed(1)}%
          </div>
          <div className="font-mono text-[10px] text-paper-dim">
            retries exhausted, no renewal
          </div>
        </div>
      </div>
    </div>
  )
}

function ARProcessCurrentMonth({
  data,
}: {
  data: DashboardSnapshot['ar_process_current_month']
}) {
  const cohortCount = data.cohort_total.count || 0
  const renewed = data.snapshot.renewed.count
  const conversion = cohortCount > 0 ? (renewed / cohortCount) * 100 : 0
  const inFlight = data.snapshot.in_retry.count
  const dead = data.snapshot.terminal_fail.count + data.snapshot.ar_disabled.count
  const awaiting = data.snapshot.awaiting.count

  return (
    <Panel accent="amber" bodyClassName="p-0">
      {/* Headline narrative — the Mailing Pull cohort. */}
      <div className="px-4 pt-3 pb-2 text-[12px] text-paper-dim font-mono leading-relaxed">
        On the 1st of {data.cohort_month}, SimpleCirc pulled mailing
        labels for all titles. Subs whose <code>issues_remaining</code>
        decremented to 0 flipped Active → Expired — that's the{' '}
        <strong className="text-amber-glow">Newly Expired</strong> cohort
        below, broken into four operationally-distinct buckets:{' '}
        <strong>Agent</strong> (non-AR agency-sold, renewal owned by the
        reseller), <strong className="text-amber-glow">AR On</strong>{' '}
        (auto_renew=1 / do_not_renew=0 — the SC Ready For Charge pool),{' '}
        <strong>AR Off Email</strong> (renewal-email campaign target),{' '}
        and <strong>AR Off No Email</strong> (direct-mail / attrition).
        These four columns sum to Newly Expired.
      </div>

      {/* Headline cohort breakdown by title. */}
      <div className="border-y border-ink-500/40">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-ink-500/40">
                <th className="label py-2 px-3 font-normal text-left">Title</th>
                <th className="label py-2 px-3 font-normal text-right">
                  Newly Expired
                  <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
                    Active → Expired this month
                  </div>
                </th>
                <th className="label py-2 px-3 font-normal text-right border-l border-ink-500/40">
                  Agent
                  <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
                    agency-sold (non-AR)
                  </div>
                </th>
                <th className="label py-2 px-3 font-normal text-right border-l-2 border-amber-glow/40 text-amber-glow">
                  AR On
                  <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
                    SC Ready For Charge pool
                  </div>
                </th>
                <th className="label py-2 px-3 font-normal text-right border-l border-ink-500/40">
                  AR Off Email
                  <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
                    email-renewal target
                  </div>
                </th>
                <th className="label py-2 px-3 font-normal text-right border-l border-ink-500/40">
                  AR Off No Email
                  <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
                    direct-mail / attrition
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.publications.map((p) => {
                const cell = data.cohort_summary_by_pub[p.slug] ?? {
                  newly_expired: 0, agent: 0, ar_on: 0,
                  ar_off_email: 0, ar_off_no_email: 0,
                }
                return (
                  <tr key={p.slug} className="border-b border-ink-500/40 hover:bg-ink-700/30">
                    <td
                      className="px-3 py-2 font-mono text-[11px] uppercase tracking-label"
                      style={{ color: publicationColor(p.slug) }}
                    >
                      {p.slug.replace(/_/g, ' ')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-mono font-semibold tabular-nums text-paper text-[16px]">
                        {fmtInt(cell.newly_expired)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right border-l border-ink-500/40">
                      <div className={`font-mono font-semibold tabular-nums text-[15px] ${cell.agent === 0 ? 'text-paper-trace' : 'text-paper-dim'}`}>
                        {fmtInt(cell.agent)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right border-l-2 border-amber-glow/40 bg-amber-glow/5">
                      <div className="font-mono font-semibold tabular-nums text-amber-glow text-[16px]">
                        {fmtInt(cell.ar_on)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right border-l border-ink-500/40">
                      <div className={`font-mono tabular-nums text-[15px] ${cell.ar_off_email === 0 ? 'text-paper-trace' : 'text-paper-dim'}`}>
                        {fmtInt(cell.ar_off_email)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right border-l border-ink-500/40">
                      <div className={`font-mono tabular-nums text-[15px] ${cell.ar_off_no_email === 0 ? 'text-paper-trace' : 'text-paper-dim'}`}>
                        {fmtInt(cell.ar_off_no_email)}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-ink-500">
                <td className="px-3 py-2 label">TOTAL</td>
                <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-paper text-[16px]">
                  {fmtInt(data.cohort_summary_totals.newly_expired)}
                </td>
                <td className="px-3 py-2 text-right border-l border-ink-500/40 font-mono font-semibold tabular-nums text-paper-dim text-[15px]">
                  {fmtInt(data.cohort_summary_totals.agent)}
                </td>
                <td className="px-3 py-2 text-right border-l-2 border-amber-glow/40 bg-amber-glow/5 font-mono font-semibold tabular-nums text-amber-glow text-[16px]">
                  {fmtInt(data.cohort_summary_totals.ar_on)}
                </td>
                <td className="px-3 py-2 text-right border-l border-ink-500/40 font-mono tabular-nums text-paper-dim text-[15px]">
                  {fmtInt(data.cohort_summary_totals.ar_off_email)}
                </td>
                <td className="px-3 py-2 text-right border-l border-ink-500/40 font-mono tabular-nums text-paper-dim text-[15px]">
                  {fmtInt(data.cohort_summary_totals.ar_off_no_email)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Drill-down narrative — this-cycle delta, not raw SC field. */}
      <div className="px-4 pt-3 pb-2 text-[12px] text-paper-dim font-mono leading-relaxed">
        Drilldown: how the AR On pool of {fmtInt(cohortCount)} is
        moving through the cycle. We compare each sub's{' '}
        <code>auto_renew_status</code> from cohort-entry day vs today —
        only subs whose status genuinely changed move out of{' '}
        <strong className="text-paper-dim">Awaiting</strong>. The raw SC
        field carries stale prior-cycle outcomes (CHARGED, RECHARGE
        FAILED) that don't reflect this cycle's progress, so we ignore
        carry-over and only count real transitions.
      </div>

      {/* Big numbers — funnel-bucket totals. */}
      <div className="px-4 py-3 border-y border-ink-500/40 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="label">Renewed</div>
          <div className="font-mono font-semibold text-radar-green text-[22px] tabular-nums">
            {fmtInt(renewed)}
          </div>
          <div className="font-mono text-[11px] text-paper-dim tabular-nums">
            {conversion.toFixed(1)}% of cohort
          </div>
        </div>
        <div>
          <div className="label">In Retry</div>
          <div className="font-mono font-semibold text-amber-glow text-[22px] tabular-nums">
            {fmtInt(inFlight)}
          </div>
          <div className="font-mono text-[11px] text-paper-dim tabular-nums">
            attempted, failed, retry budget left
          </div>
        </div>
        <div>
          <div className="label">Terminal / Disabled</div>
          <div className="font-mono font-semibold text-red-400 text-[22px] tabular-nums">
            {fmtInt(dead)}
          </div>
          <div className="font-mono text-[11px] text-paper-dim tabular-nums">
            FIFTH/FINAL or AR turned off
          </div>
        </div>
        <div>
          <div className="label">Awaiting</div>
          <div className="font-mono font-semibold text-paper text-[22px] tabular-nums">
            {fmtInt(awaiting)}
          </div>
          <div className="font-mono text-[11px] text-paper-dim tabular-nums">
            still queued for SC this cycle
          </div>
        </div>
      </div>

      {/* Per-status breakdown */}
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-ink-500">
              {AR_STATUS_ORDER.map((s) => (
                <th
                  key={s}
                  className={`label py-2 px-3 font-normal text-left ${AR_STATUS_TONE[s]}`}
                >
                  <div>{AR_STATUS_LABEL[s]}</div>
                  <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
                    {AR_STATUS_NOTE[s]}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {AR_STATUS_ORDER.map((s) => {
                const cell = data.snapshot[s]
                const pct = cohortCount > 0 ? (cell.count / cohortCount) * 100 : 0
                return (
                  <td key={s} className="px-3 py-2.5">
                    <div className={`font-mono font-semibold tabular-nums ${AR_STATUS_TONE[s]} text-[18px]`}>
                      {fmtInt(cell.count)}
                    </div>
                    <div className="font-mono text-[11px] tabular-nums text-paper-dim">
                      {pct.toFixed(1)}% · {fmtMoney(cell.revenue, true)}
                    </div>
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Per-publication breakdown — funnel-bucket counts per title.
          Cohort total ("Ready For Charge") is the headline number on the
          left; the funnel sub-buckets to the right show this-cycle
          progress (sum equals the cohort total). */}
      <div className="border-t border-ink-500/40">
        <div className="px-4 pt-3 pb-1 label">By Title · Cycle Funnel</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-ink-500/40">
                <th className="label py-2 px-3 font-normal text-left">Title</th>
                <th className="label py-2 px-3 font-normal text-right border-l-2 border-amber-glow/40 text-amber-glow">
                  Ready For Charge
                  <div className="font-mono text-[9px] text-paper-trace mt-0.5 normal-case tracking-normal">
                    SC AR pool
                  </div>
                </th>
                <th className="label py-2 px-3 font-normal text-center border-l border-ink-500/40 text-paper-trace italic" colSpan={AR_STATUS_ORDER.length}>
                  &mdash; this-cycle delta vs entry status &mdash;
                </th>
              </tr>
              <tr className="border-b border-ink-500/40">
                <th></th>
                <th className="border-l-2 border-amber-glow/40"></th>
                {AR_STATUS_ORDER.map((s) => (
                  <th
                    key={s}
                    className={`label py-2 px-3 font-normal text-right border-l border-ink-500/40 ${AR_STATUS_TONE[s]}`}
                  >
                    {AR_STATUS_LABEL[s]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.publications.map((p) => {
                const cell = data.snapshot_by_pub[p.slug] ?? null
                if (!cell) return null
                return (
                  <tr key={p.slug} className="border-b border-ink-500/40 hover:bg-ink-700/30">
                    <td
                      className="px-3 py-2 font-mono text-[11px] uppercase tracking-label"
                      style={{ color: publicationColor(p.slug) }}
                    >
                      {p.slug.replace(/_/g, ' ')}
                    </td>
                    {/* Cohort total — the headline number for each title. */}
                    <td className="px-3 py-2 text-right border-l-2 border-amber-glow/40 bg-amber-glow/5">
                      <div className="font-mono font-semibold tabular-nums text-amber-glow text-[15px]">
                        {fmtInt(cell.total.count)}
                      </div>
                      {cell.total.revenue > 0 && (
                        <div className="font-mono text-[10px] tabular-nums text-radar-green">
                          {fmtMoney(cell.total.revenue, true)}
                        </div>
                      )}
                    </td>
                    {/* Status sub-buckets — sum equals the Ready For Charge total. */}
                    {AR_STATUS_ORDER.map((s) => (
                      <td
                        key={s}
                        className="px-3 py-2 text-right border-l border-ink-500/40"
                      >
                        <div
                          className={`font-mono tabular-nums ${
                            cell.by_status[s].count === 0 ? 'text-paper-trace' : AR_STATUS_TONE[s]
                          }`}
                        >
                          {fmtInt(cell.by_status[s].count)}
                        </div>
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

    </Panel>
  )
}

function EventStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'success' || status === 'ok' ? 'ok' :
    status === 'duplicate' ? 'muted' :
    status === 'failed' || status === 'error' ? 'err' :
    status === 'pending' || status === 'running' ? 'active' : 'warn'

  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot tone={tone} />
      <span className="font-mono text-[10px] uppercase tracking-label text-paper-dim">
        {status}
      </span>
    </span>
  )
}
